/**
 * XP Service for Poker Reborn
 *
 * Handles communication with the XP service (GLID "xp").
 * Manages level progression, XP gains, and level-up events.
 *
 * Follows the DailyBonusService singleton pattern:
 * - Listens to NetworkService messages from GLID "xp"
 * - Emits typed events via EventEmitter<XpServiceEvents>
 * - Server pushes xp_gain after each hand, level_up on milestones
 *
 * @see docs/features/progress/XP_LEVELS.md - Feature design
 * @see docs/features/progress/XP-IMPL-CLIENT.md - Client implementation spec
 */

import { EventEmitter } from './EventEmitter';
import { networkService } from './NetworkService';
import { GLIDS } from '@/config/network';
import { updateXpState } from '@/state/AppState';
import { log } from '@/utils/log';

// =============================================================================
// TYPES
// =============================================================================

/** XP status from server (response to # xp status) */
export interface XpStatus {
  /** Current player level */
  level: number;
  /** Cumulative total XP earned */
  xpTotal: number;
  /** XP threshold for current level */
  xpLevelStart: number;
  /** XP threshold for next level */
  xpLevelEnd: number;
  /** XP earned within current level */
  xpInLevel: number;
  /** XP remaining to reach next level */
  xpNeeded: number;
  /** Progress percentage within current level (0-100) */
  pct: number;
  /** Milestone title (e.g., "Regular", "Skilled", "Expert") or null */
  title: string | null;
}

/** XP gain event (pushed after each hand) */
export interface XpGain {
  /** XP earned this hand */
  xp: number;
  /** New cumulative total XP */
  xpTotal: number;
  /** Current level (may have changed) */
  level: number;
  /** Progress percentage in current level (0-100) */
  pct: number;
}

/** Level-up reward details */
export interface LevelUpReward {
  /** Chips awarded */
  chips: number;
  /** Avatar frame unlock (e.g., "bronze", "silver") or null */
  frame: string | null;
  /** Title unlock (e.g., "Regular", "Skilled") or null */
  title: string | null;
  /** Badge unlocked or null */
  badge: boolean | null;
}

/** Level-up event (pushed when player levels up) */
export interface LevelUpData {
  /** Previous level */
  oldLevel: number;
  /** New level */
  newLevel: number;
  /** Reward for reaching the new level */
  reward: LevelUpReward;
  /** XP needed to reach the level after newLevel */
  xpNeededNext: number;
}

/** Events emitted by XpService */
export interface XpServiceEvents {
  /** Status received (response to # xp status) */
  statusUpdated: XpStatus;
  /** XP gained after a hand */
  xpGained: XpGain;
  /** Player leveled up */
  leveledUp: LevelUpData;
  /** Error from XP service */
  error: { code: string; message: string };
}

// =============================================================================
// XP SERVICE
// =============================================================================

class XpService extends EventEmitter<XpServiceEvents> {
  private static instance: XpService | null = null;
  private lastStatus: XpStatus | null = null;

  private constructor() {
    super();
    this.setupNetworkHandlers();
  }

  /** Get singleton instance */
  static getInstance(): XpService {
    if (!XpService.instance) {
      XpService.instance = new XpService();
    }
    return XpService.instance;
  }

  // ===========================================================================
  // PUBLIC API - Commands
  // ===========================================================================

  /**
   * Request current XP status.
   * Call after authentication completes.
   * Triggers 'statusUpdated' event.
   */
  requestStatus(): void {
    if (!networkService.isConnected()) {
      log.xp.warning`Cannot request XP status: not connected`;
      return;
    }
    log.xp.info`Requesting XP status...`;
    networkService.sendCommand(GLIDS.XP, '# status');
  }

  // ===========================================================================
  // PUBLIC API - State
  // ===========================================================================

  /** Get last known XP status (null before first request) */
  getLastStatus(): XpStatus | null {
    return this.lastStatus;
  }

  // ===========================================================================
  // PRIVATE - Network Handlers
  // ===========================================================================

  private setupNetworkHandlers(): void {
    networkService.on('message', ({ from, body }) => {
      if (from !== GLIDS.XP) return;

      log.xp.debug`XP message received: ${body}`;

      try {
        const message = JSON.parse(body) as Record<string, unknown>;

        switch (message.a) {
          case 'xp_status':
            this.handleStatusResponse(message);
            break;

          case 'xp_gain':
            this.handleXpGain(message);
            break;

          case 'level_up':
            this.handleLevelUp(message);
            break;

          case 'xp_error':
            this.handleErrorResponse(message);
            break;

          default:
            log.xp.warning`Unknown XP action: ${message.a}`;
        }
      } catch {
        log.xp.warning`Failed to parse XP message: ${body}`;
      }
    });
  }

  private handleStatusResponse(message: Record<string, unknown>): void {
    const status: XpStatus = {
      level: Number(message.level ?? 1),
      xpTotal: Number(message.xp_total ?? 0),
      xpLevelStart: Number(message.xp_level_start ?? 0),
      xpLevelEnd: Number(message.xp_level_end ?? 0),
      xpInLevel: Number(message.xp_in_level ?? 0),
      xpNeeded: Number(message.xp_needed ?? 0),
      pct: Number(message.pct ?? 0),
      title: (message.title as string) ?? null,
    };

    this.lastStatus = status;
    updateXpState(status.level, status.pct);
    this.emit('statusUpdated', status);

    log.xp.info`Status: level ${status.level}, ${status.pct}% to next, title=${status.title ?? 'none'}`;
  }

  private handleXpGain(message: Record<string, unknown>): void {
    const xpGain: XpGain = {
      xp: Number(message.xp ?? 0),
      xpTotal: Number(message.xp_total ?? 0),
      level: Number(message.level ?? 1),
      pct: Number(message.pct ?? 0),
    };

    // Update cached status with new values
    if (this.lastStatus) {
      this.lastStatus = {
        ...this.lastStatus,
        level: xpGain.level,
        xpTotal: xpGain.xpTotal,
        pct: xpGain.pct,
      };
    }

    updateXpState(xpGain.level, xpGain.pct);
    this.emit('xpGained', xpGain);

    log.xp.debug`XP gained: +${xpGain.xp}, total=${xpGain.xpTotal}, level=${xpGain.level}, ${xpGain.pct}%`;
  }

  private handleLevelUp(message: Record<string, unknown>): void {
    const rawReward = message.reward as Record<string, unknown> | undefined;

    const levelUpData: LevelUpData = {
      oldLevel: Number(message.old_level ?? 1),
      newLevel: Number(message.new_level ?? 1),
      reward: {
        chips: Number(rawReward?.chips ?? 0),
        frame: (rawReward?.frame as string) ?? null,
        title: (rawReward?.title as string) ?? null,
        badge: (rawReward?.badge as boolean) ?? null,
      },
      xpNeededNext: Number(message.xp_needed_next ?? 0),
    };

    // Update cached status level
    if (this.lastStatus) {
      this.lastStatus = {
        ...this.lastStatus,
        level: levelUpData.newLevel,
        pct: 0,
      };
    }

    updateXpState(levelUpData.newLevel, 0);
    this.emit('leveledUp', levelUpData);

    log.xp.info`Level up! ${levelUpData.oldLevel} -> ${levelUpData.newLevel}, +${levelUpData.reward.chips} chips`;
  }

  private handleErrorResponse(message: Record<string, unknown>): void {
    const errorCode = String(message.code ?? 'unknown');
    const errorMessage = String(message.message ?? 'Unknown error');

    log.xp.warning`XP error: ${errorCode} - ${errorMessage}`;
    this.emit('error', { code: errorCode, message: errorMessage });
  }
}

/** Singleton XP service instance */
export const xpService = XpService.getInstance();
